import React, { useEffect, useState } from 'react';
import { CCard, CCardBody, CCardHeader, CCardTitle, CFormGroup, CInput } from '@coreui/react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import _t from 'counterpart';
import ButtonWithLoader from '../../components/ButtonWithLoader';
import { handleFormError } from '../../helpers';
import toast from 'react-hot-toast';
import { fetchCreditValidityDays, updateCreditValidityDays } from '../../services/BackendService';

const QUERY_KEY = 'credit-validity-days';

const CreditValiditySetting = () => {
  const [days, setDays] = useState<number>(0);
  const [originalValue, setOriginalValue] = useState<number>(0);

  const queryClient = useQueryClient();

  const settingQuery = useQuery(QUERY_KEY, fetchCreditValidityDays, {
    retry: false,
    refetchOnWindowFocus: false,
  });

  const updateMutation = useMutation(() => updateCreditValidityDays(days), {
    onSuccess: async () => {
      toast.success(_t('credit-validity-setting.successfully-updated'));
      await queryClient.invalidateQueries([QUERY_KEY]);
      setOriginalValue(days);
    },
    onError: async (e: any) => {
      handleFormError(e);
    },
  });

  useEffect(() => {
    if (settingQuery.data !== undefined) {
      const value = Number(settingQuery.data.creditValidityDays) || 0;
      setDays(value);
      setOriginalValue(value);
    }
  }, [settingQuery.data]);

  const hasChanges = days !== originalValue;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    setDays(isNaN(value) || value < 0 ? 0 : value);
  };

  if (settingQuery.isLoading) {
    return (
      <CCard className="mt-4">
        <CCardHeader>
          <CCardTitle>{_t('credit-validity-setting.title')}</CCardTitle>
          <p className="mb-0 mt-1 small">{_t('credit-validity-setting.description')}</p>
        </CCardHeader>
        <CCardBody>
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </CCardBody>
      </CCard>
    );
  }

  return (
    <CCard className="mt-4">
      <CCardHeader>
        <CCardTitle>{_t('credit-validity-setting.title')}</CCardTitle>
        <p className="mb-0 mt-1 small">{_t('credit-validity-setting.description')}</p>
      </CCardHeader>
      <CCardBody>
        <CFormGroup>
          <label htmlFor="credit-validity-days">{_t('credit-validity-setting.label')}</label>
          <CInput
            id="credit-validity-days"
            type="number"
            min={0}
            step={1}
            value={days}
            onChange={handleChange}
            disabled={settingQuery.isLoading || updateMutation.isLoading}
            style={{ maxWidth: '200px' }}
          />
          <small className="text-muted mt-2 d-block">
            {days === 0
              ? _t('credit-validity-setting.unlimited')
              : _t('credit-validity-setting.days-set', { count: days })}
          </small>
          <small className="text-warning mt-2 d-block">
            {_t('credit-validity-setting.warning')}
          </small>
        </CFormGroup>

        <ButtonWithLoader
          isLoading={updateMutation.isLoading}
          onClick={() => updateMutation.mutate()}
          buttonColor="primary"
          spinnerColor="secondary"
          title={_t('action.save')}
          className="btn-md"
          showText={false}
          disabled={!hasChanges}
        />
      </CCardBody>
    </CCard>
  );
};

export default CreditValiditySetting;
